"use client";

import { motion, useInView } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import { Drumstick, LeafyGreen, Snowflake } from "lucide-react";
import Image from "next/image";
import { useRef } from "react";
import { IMAGES } from "@/lib/constants/images";

interface BrandValue {
	icon: LucideIcon;
	title: string;
	description: string;
}

const brandValues: BrandValue[] = [
	{
		icon: Drumstick,
		title: "PROTEIN-PACKED",
		description:
			"We don't skimp on portions. Our meals are loaded with quality protein to keep you full, focused, and feeling strong — whether you're training or just tackling life.",
	},
	{
		icon: LeafyGreen,
		title: "CLEAN INGREDIENTS",
		description:
			"No seed oils, no preservatives, no shortcuts. Just real ingredients you can trust — because we care about what you're putting in your body as much as you do.",
	},
	{
		icon: Snowflake,
		title: "FROZEN = FREEDOM",
		description:
			"Life moves fast — we make it easier. Our meals are frozen fresh, so you've always got clean, protein-packed fuel in minutes. No prep. No pressure. Just grab, heat, and eat.",
	},
];

const containerVariants = {
	hidden: { opacity: 0 },
	show: {
		opacity: 1,
		transition: {
			staggerChildren: 0.15,
		},
	},
};

const itemVariants = {
	hidden: { opacity: 0, y: 30 },
	show: {
		opacity: 1,
		y: 0,
		transition: { duration: 0.5, ease: "easeOut" as const },
	},
};

export function BrandValuesSection() {
	const ref = useRef<HTMLElement>(null);
	const isInView = useInView(ref, { once: true, margin: "-100px" });

	return (
		<section ref={ref} className="relative py-24 md:py-32 overflow-hidden">
			<div className="absolute inset-0">
				<Image
					src={IMAGES.home.brandValues}
					alt="Fresh ingredients background"
					fill
					className="object-cover"
					sizes="100vw"
					quality={60}
					priority={false}
				/>
				<div className="absolute inset-0 bg-white/85 dark:bg-black/80" />
			</div>

			<div className="relative z-10 container mx-auto px-4">
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={isInView ? { opacity: 1, y: 0 } : {}}
					transition={{ duration: 0.5 }}
					className="text-center mb-16"
				>
					<span className="inline-block text-sm font-semibold tracking-widest uppercase text-primary mb-3">
						What We Stand For
					</span>
					<h2 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-zinc-900 dark:text-white mb-5">
						BUILT DIFFERENT
					</h2>
					<p className="text-base md:text-lg text-zinc-600 dark:text-zinc-300 max-w-2xl mx-auto leading-relaxed">
						Every Ice Age Meal is designed with one mission — fuel real people
						with real food, without compromise.
					</p>
				</motion.div>

				<motion.div
					variants={containerVariants}
					initial="hidden"
					animate={isInView ? "show" : "hidden"}
					className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto"
				>
					{brandValues.map((value) => {
						const Icon = value.icon;
						return (
							<motion.div
								key={value.title}
								variants={itemVariants}
								className="group relative rounded-2xl border border-zinc-200 dark:border-white/10 bg-white/70 dark:bg-white/5 backdrop-blur-md p-8 md:p-10 text-center transition-all duration-500 hover:bg-white/90 dark:hover:bg-white/10 hover:border-zinc-300 dark:hover:border-white/20 hover:shadow-2xl"
							>
								<div className="mx-auto mb-6 w-14 h-14 rounded-xl bg-primary/15 flex items-center justify-center transition-transform duration-500 group-hover:scale-110">
									<Icon className="w-7 h-7 text-primary" />
								</div>

								<h3 className="text-xl md:text-2xl font-black tracking-tight text-zinc-900 dark:text-white mb-3">
									{value.title}
								</h3>

								<p className="text-sm md:text-base text-zinc-600 dark:text-zinc-400 leading-relaxed">
									{value.description}
								</p>

								<div className="absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 w-0 bg-primary transition-all duration-500 group-hover:w-2/3 rounded-full" />
							</motion.div>
						);
					})}
				</motion.div>
			</div>
		</section>
	);
}
